import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info, X } from "lucide-react";
import {
  api,
  ShoppingSessionOutcomeFilter,
  ShoppingSessionRow,
} from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InfoTip, LabelWithTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import EntryGroupsSplit from "@/components/EntryGroupsSplit";
import DailyTrendChart from "@/components/DailyTrendChart";
import { cn, formatPLN } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";

const OUTCOME_OPTIONS: { id: ShoppingSessionOutcomeFilter; label: string }[] = [
  { id: "all", label: "Wszystkie" },
  { id: "purchased", label: "Zakup" },
  { id: "abandoned", label: "Porzucenie" },
];

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

function formatMs(ms: number | null | undefined): string {
  if (ms == null) return "-";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export default function ShoppingSessionAnalysis() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");
  const [outcome, setOutcome] = useState<ShoppingSessionOutcomeFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["shopping-sessions", period, outcome],
    queryFn: () =>
      api.siteAnalyticsShoppingSessions({
        ...journeyWindowParams(period),
        outcome,
        limit: 50,
        offset: 0,
      }),
    retry: false,
  });

  const detailQuery = useQuery({
    queryKey: ["shopping-session-detail", selectedId],
    queryFn: () => api.siteAnalyticsShoppingSessionDetail(selectedId!),
    enabled: Boolean(selectedId),
    retry: false,
  });

  const data = query.data;
  const summary = data?.summary;
  const trends = data?.trends?.days ?? [];

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="sesje" />

      <div className="flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="mx-1 text-muted-foreground">|</span>
        {OUTCOME_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === outcome ? "default" : "outline"}
            onClick={() => setOutcome(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {data?.period?.label_pl ?? "czas PL"}
          {" · "}
          Kliknij wiersz, żeby zobaczyć przebieg sesji.
        </span>
      </div>

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie sesji…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych.
          </CardContent>
        </Card>
      )}

      {summary && (
        <>
          <EntryGroupsSplit
            paidCount={summary.paid_entries ?? 0}
            otherCount={summary.other_entries ?? 0}
            title="Wejścia przed koszykiem: płatne vs pozostałe"
            subtitle="Sesje z koszykiem, które weszły z reklamy (?via=ad) vs reszta kanałów."
            showModeToggle={false}
            defaultMode="all"
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi tipId="cart_sessions_ours" value={String(summary.cart_sessions)} />
            <Kpi tipId="purchases_on_path" value={String(summary.purchased)} />
            <Kpi tipId="abandoned_carts" value={String(summary.abandoned)} />
            <Kpi tipId="cart_to_purchase" value={pct(summary.cart_to_purchase_rate)} />
          </div>

          <DailyTrendChart
            title="Trend: koszyki vs zakupy vs przychód"
            subtitle={
              data?.trends?.delta?.cart_sessions_delta_pct != null
                ? `Koszyki vs wcześniejsza połowa okna: ${data.trends.delta.cart_sessions_delta_pct > 0 ? "+" : ""}${data.trends.delta.cart_sessions_delta_pct}%`
                : undefined
            }
            data={trends.map((d) => ({
              date: d.date,
              cart_sessions: d.cart_sessions ?? 0,
              purchase_sessions: d.purchase_sessions ?? 0,
              easycart_revenue: d.easycart_revenue ?? 0,
            }))}
            series={[
              { key: "cart_sessions", label: "Koszyki", color: "#6366f1" },
              {
                key: "purchase_sessions",
                label: "Zakupy (ścieżka)",
                color: "#22c55e",
                rightAxis: true,
              },
              {
                key: "easycart_revenue",
                label: "Przychód EC",
                color: "#f59e0b",
                format: "pln",
              },
            ]}
            mode="line"
          />

          {data.insights && data.insights.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Wnioski</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {data.insights.map((ins, i) => (
                    <li key={i} className="flex gap-2">
                      {ins.severity === "warning" ? (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      ) : (
                        <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                      )}
                      <span>{ins.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-base">Sesje z koszykiem</CardTitle>
                <InfoTip tipId="shopping_session_list" />
              </div>
              <p className="text-xs text-muted-foreground">
                {data.pagination.total} sesji w oknie (pokazano {data.sessions.length})
              </p>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Czas</th>
                    <th className="px-4 py-2 font-medium">Wynik</th>
                    <th className="px-4 py-2 font-medium">Produkt</th>
                    <th className="px-4 py-2 font-medium">Kanał</th>
                    <th className="px-4 py-2 font-medium">Profil</th>
                    <th className="px-4 py-2 font-medium">Ścieżka</th>
                    <th className="px-4 py-2 font-medium text-right">Kwota</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sessions.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        Brak sesji w tym oknie.
                      </td>
                    </tr>
                  )}
                  {data.sessions.map((row) => (
                    <SessionRow
                      key={row.kz_jid}
                      row={row}
                      selected={selectedId === row.kz_jid}
                      onSelect={() => setSelectedId(row.kz_jid)}
                    />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {selectedId && (
        <SessionDrawer
          sessionId={selectedId}
          loading={detailQuery.isLoading}
          error={detailQuery.isError}
          data={detailQuery.data}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function Kpi({ tipId, value, hint }: { tipId: MetricTipId; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground">
          <LabelWithTip tipId={tipId} />
        </div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function SessionRow({
  row,
  selected,
  onSelect,
}: {
  row: ShoppingSessionRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <tr
      className={cn(
        "cursor-pointer border-b transition-colors hover:bg-muted/50",
        selected && "bg-muted/60",
      )}
      onClick={onSelect}
    >
      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">
        {row.started_at_pl || "-"}
      </td>
      <td className="px-4 py-2.5">
        <Badge variant={row.outcome === "Zakup" ? "default" : "secondary"}>
          {row.outcome}
        </Badge>
      </td>
      <td className="max-w-[140px] truncate px-4 py-2.5">{row.product_label}</td>
      <td className="max-w-[120px] truncate px-4 py-2.5">{row.traffic_label}</td>
      <td className="max-w-[120px] truncate px-4 py-2.5">{row.profile_label}</td>
      <td className="max-w-[220px] truncate px-4 py-2.5 text-xs text-muted-foreground">
        {row.path_summary}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
        {row.revenue != null && row.revenue > 0 ? formatPLN(row.revenue) : "-"}
      </td>
    </tr>
  );
}

function SessionDrawer({
  sessionId,
  loading,
  error,
  data,
  onClose,
}: {
  sessionId: string;
  loading: boolean;
  error: boolean;
  data: Awaited<ReturnType<typeof api.siteAnalyticsShoppingSessionDetail>> | undefined;
  onClose: () => void;
}) {
  const session = data?.session;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Przebieg sesji</h2>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {session?.kz_jid_short ?? sessionId}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Zamknij">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && <p className="text-sm text-muted-foreground">Wczytywanie…</p>}
          {error && (
            <p className="text-sm text-muted-foreground">Nie udało się wczytać detalu.</p>
          )}
          {session && (
            <div className="grid gap-4">
              {data?.quality?.weak_attribution && (
                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Słaba atrybucja: zakup jest w systemie, ale krótka lub pusta historia na
                    landingu.
                  </span>
                </div>
              )}

              <div className="grid gap-2 text-sm">
                <MetaRow label="Czas" value={session.started_at_pl || "-"} />
                <MetaRow label="Wynik" value={session.outcome} />
                <MetaRow label="Profil" value={session.profile_label} />
                <MetaRow label="Kanał" value={session.traffic_label} />
                <MetaRow label="Wejście" value={session.entry_label} />
                {session.via && <MetaRow label="?via=" value={session.via} />}
                <MetaRow label="Czas do koszyka" value={formatMs(session.ms_to_checkout)} />
                <MetaRow label="Aktywny czas" value={formatMs(session.active_ms)} />
                <MetaRow
                  label="Scroll max"
                  value={session.max_scroll != null ? `${session.max_scroll}%` : "-"}
                />
              </div>

              {data?.paths && (
                <div className="text-sm">
                  <div className="mb-1 font-medium">Ścieżka stron</div>
                  <p className="text-muted-foreground">{data.paths.page_path}</p>
                </div>
              )}

              {data?.order && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-sm">
                  <div className="font-medium text-emerald-900">Zamówienie EasyCart</div>
                  <p className="mt-1 text-emerald-800">
                    {data.order.product_label} · {formatPLN(data.order.revenue)}
                  </p>
                  <p className="mt-1 font-mono text-xs text-emerald-700">
                    #{data.order.order_ref}
                  </p>
                  <p className="text-xs text-emerald-700">{data.order.ordered_at_pl}</p>
                </div>
              )}

              <div>
                <div className="mb-3 flex items-center gap-1.5 text-sm font-medium">
                  Timeline zdarzeń
                  <InfoTip tipId="session_timeline" />
                </div>
                <ol className="relative border-l border-slate-200 pl-4">
                  {(data?.timeline ?? []).map((ev, i) => (
                    <li key={`${ev.ts}-${i}`} className="mb-4 ml-2">
                      <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-slate-400" />
                      <div className="text-xs text-muted-foreground">{ev.ts_pl}</div>
                      <div className="font-medium">{ev.label_pl}</div>
                      {(ev.page_id || ev.section_id) && (
                        <div className="text-xs text-muted-foreground">
                          {[ev.page_id, ev.section_id].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      {ev.ms_since_session_start != null && (
                        <div className="text-[11px] text-muted-foreground">
                          +{formatMs(ev.ms_since_session_start)} od startu
                        </div>
                      )}
                    </li>
                  ))}
                  {(data?.timeline ?? []).length === 0 && (
                    <li className="text-sm text-muted-foreground">Brak zdarzeń w timeline.</li>
                  )}
                </ol>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
